import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles({
  container: {
    backgroundColor: "#FFFFFF",
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 0,
  },
  scrollContent: {
    maxHeight: "360px",
    overflowY: "auto",
    padding: "16px 20px",
    boxSizing: "border-box",
  },
  timelineItem: {
    display: "flex",
    position: "relative",
    paddingBottom: "14px",
  },
  dateCol: {
    width: "95px",
    flexShrink: 0,
    fontSize: "0.8125rem",
    color: "#555555",
    fontWeight: 500,
    paddingTop: "4px",
  },
  trackCol: {
    width: "36px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    flexShrink: 0,
  },
  verticalLine: {
    position: "absolute",
    top: "28px",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "2px",
    backgroundColor: "#e0e0e0",
    zIndex: 0,
  },
  avatar: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    backgroundColor: "#007BC0",
    color: "#FFFFFF",
    fontSize: "0.75rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    flexShrink: 0,
  },
  contentCol: {
    flex: 1,
    minWidth: 0,
    paddingLeft: "12px",
  },
  userName: {
    color: "#000000",
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginTop: "4px",
    flexWrap: "wrap",
  },
  badgeApproved: {
    backgroundColor: "#B8EFC9",
    color: "#000000",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  badgeRejected: {
    backgroundColor: "#FCE8E6",
    color: "#000000",
    borderRadius: "12px",
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  opType: {
    fontWeight: 700,
    fontSize: "0.8125rem",
    color: "#000000",
  },
  detailsText: {
    fontSize: "0.75rem",
    color: "#555555",
    marginTop: "4px",
  },
  commentsBox: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    padding: "8px 12px",
    marginTop: "8px",
  },
  commentsTitle: {
    fontWeight: 700,
    fontSize: "0.75rem",
    color: "#000000",
    marginBottom: "2px",
  },
  commentsText: {
    fontSize: "0.75rem",
    color: "#333333",
    lineHeight: 1.4,
  },
  separator: {
    borderBottom: "1px solid #f0f0f0",
    marginTop: "14px",
    width: "100%",
  },
  emptyContainer: {
    padding: "2rem 1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#555555",
    fontSize: "0.875rem",
  },
  loadingContainer: {
    padding: "2rem 1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

function getInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatCurrency(val) {
  return `R$ ${Number(val || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function ApprovalHistory({ items = [], loading = false }) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.container}>
      <div className={classes.scrollContent}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={32} />
          </div>
        ) : !items || items.length === 0 ? (
          <div className={classes.emptyContainer}>
            <span>{t("ApprovalHistory.EmptyList")}</span>
          </div>
        ) : (
          items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isApproved = item.action === "APPROVED";
            const initials = getInitials(item.userName);
            const actionText = isApproved
              ? `${t("ApprovalHistory.ActionApproved")} | ${item.time}`
              : `${t("ApprovalHistory.ActionRejected")} | ${item.time}`;

            return (
              <div key={item.id || index} className={classes.timelineItem}>
                <div className={classes.dateCol}>{item.date}</div>

                <div className={classes.trackCol}>
                  <div className={classes.avatar}>{initials}</div>
                  {!isLast && <div className={classes.verticalLine} />}
                </div>

                <div className={classes.contentCol}>
                  <div className={classes.userName}>{item.userName}</div>

                  <div className={classes.statusRow}>
                    <span
                      className={
                        isApproved ? classes.badgeApproved : classes.badgeRejected
                      }
                    >
                      {actionText}
                    </span>
                    <span className={classes.opType}>{item.opType}</span>
                  </div>

                  <div className={classes.detailsText}>
                    {t("ApprovalHistory.UnitValueLabel")}: {formatCurrency(item.unitValue)} |{" "}
                    {t("ApprovalHistory.TotalValueLabel")}: {formatCurrency(item.totalValue)}
                  </div>

                  {!isApproved && item.comment && (
                    <div className={classes.commentsBox}>
                      <div className={classes.commentsTitle}>
                        {t("ApprovalHistory.CommentsTitle")}
                      </div>
                      <div className={classes.commentsText}>{item.comment}</div>
                    </div>
                  )}

                  {!isLast && <div className={classes.separator} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
